import React, { useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import fileDownload from 'js-file-download';

import styles from '../styles';
import { getSamples, clearSamples, clearSampleFiles } from '../Actions';
import {
  errorNotification,
  errorNotificationOnBlob,
  successNotification,
} from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';

const SampleList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const samples = useSelector(state => state.samples);

  useEffect(() => {
    dispatch(getSamples());
    return () => {
      dispatch(clearSamples());
      dispatch(clearSampleFiles());
    };
  }, []);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/samples/${id}`);
      successNotification('Sample deleted');
      dispatch(getSamples());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const onFileDownload = useCallback(async id => {
    try {
      const { data } = await Api.getFile(`/samples/download/${id}`);
      fileDownload(data, `sample-${id}.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotificationOnBlob(err);
    }
  }, []);

  const preivewColumn = useMemo(
    () => record =>
      (
        <Tooltip placement="bottom" title="View">
          <Link to={`/samples/preview/${record.id}`}>
            <FileOutlined className={classes.tableIcon} />
          </Link>
        </Tooltip>
      ),
    []
  );

  const downloadColumn = useMemo(
    () => record =>
      (
        <Tooltip placement="bottom" title="Download">
          <DownloadOutlined
            className={classes.tableIcon}
            onClick={() => onFileDownload(record.id)}
          />
        </Tooltip>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteSample) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const _columns = [
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Products',
        dataIndex: 'products',
        sorter: sorter('products'),
        onFilter: onFilter('products'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'View',
        render: preivewColumn,
      },
    ];

    if (permissions.includes(constants.permissions.deleteSample)) {
      _columns.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    _columns.push(
      {
        title: 'Download',
        // width: '6%',
        render: downloadColumn,
      },
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      }
    );

    return _columns;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1800, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Sample"
        moduleLink="/samples/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={samples}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

SampleList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(SampleList);
